import React, { useState } from 'react';
import PropTypes from 'prop-types';

import { Button, Col, Form, Row, Table } from 'react-bootstrap';
import { css, StyleSheet } from 'aphrodite';
import Select from 'react-select';
import ToggleSwitch from 'react-switch';

import { backgroundGrey, grape } from 'lib/css/colors';
import { BRANDS_DP_PRODUCTS, GLOBAL_VENDORS_FOR_DROPDOWN } from 'lib/networking/endpoints';
import Container from 'components/shared/Container';
import { DEFAULT_PAGE_SIZE } from 'lib/constants';
import { FILE_EXPORTS_GENERATE_REPORTS } from 'lib/networking/endpoints';
import LoadingSpinner from 'components/shared/LoadingSpinner';
import PaginationComponent from 'components/shared/PaginationComponent';
import pointerOnHover from 'lib/css/pointerOnHover';
import { ReportType } from 'lib/enums/ReportGeneratrionEnums';
import Swal from 'sweetalert2';
import SwalDialog from 'lib/utils/toast';
import UserPermission from 'lib/enums/UserPermission';
import useUserPermissions from 'lib/hooks/useUserPermissions';
import useGet from 'lib/hooks/useGet';
import usePost from 'lib/hooks/usePost';

function LinkedDpProductsView({ brands }) {
  const hasUserPermissions = useUserPermissions();
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(DEFAULT_PAGE_SIZE);
  const [selectedBrands, setSelectedBrands] = useState([]);
  const [selectedVendor, setSelectedVendor] = useState(null);
  const [unmapped_only, setUnmappedOnly] = useState(false);

  const { data: { data: globalVendors } = {} } = useGet(GLOBAL_VENDORS_FOR_DROPDOWN);
  const { data: { dp_products = [] } = {}, loading: loadingBrandDpProducts } = useGet(
    BRANDS_DP_PRODUCTS,
    {
      brand_ids: selectedBrands?.map(brand => brand.value),
      global_vendor_id: selectedVendor?.value,
      limit: itemsPerPage,
      offset: itemsPerPage * (currentPage - 1),
      unmapped_only: unmapped_only,
    },
  );

  const { data: { dp_products_count } = 0 } = useGet(BRANDS_DP_PRODUCTS, {
    brand_ids: selectedBrands?.map(brand => brand.value),
    global_vendor_id: selectedVendor?.value,
    limit: itemsPerPage,
    offset: itemsPerPage * (currentPage - 1),
    unmapped_only: unmapped_only,
    count_only: true,
  });
  const { postData: exportBrandDpProducts, loading: exportingBrandDpProducts } = usePost(
    FILE_EXPORTS_GENERATE_REPORTS,
    () =>
      Swal.fire({
        icon: 'success',
        html: `
        <div>
          The export csv file will be generated in a while.
          You can download the generated file from\n
          <a
            href="/file-exports"
            rel="noopener noreferrer"
            target="_blank"
          >
            File Exports Page
          </a>
        </div>`,
        title: 'Success',
        position: 'center',
      }),
    () =>
      SwalDialog(
        'error',
        'An error occurred while trying to export the brand dp products',
        'Error',
        'center',
      ),
  );

  return (
    <Container>
      <div>
        <Row>
          <Col md={2}>
            <Form.Group>
              <Form.Label>Select Brand(s)</Form.Label>
              <Select
                isMulti
                value={selectedBrands}
                options={brands.map(brand => ({
                  label: brand.name,
                  value: brand.id,
                }))}
                onChange={e => setSelectedBrands(e || [])}
                placeholder="Select brand(s)..."
              />
            </Form.Group>
          </Col>
          <Col md={3}>
            <Form.Group>
              <Form.Label>Select Distributor</Form.Label>
              <Select
                value={selectedVendor}
                options={globalVendors?.map(vendor => ({
                  value: vendor.id,
                  label: vendor.name,
                }))}
                onChange={e => setSelectedVendor(e)}
                placeholder="Search distributor..."
                isSearchable
                isClearable
              />
            </Form.Group>
          </Col>
          <Col md={2}>
            <Form.Group>
              <Form.Label>Show Unmapped Only</Form.Label>
              <ToggleSwitch
                onColor={grape}
                className={css(styles.value)}
                checked={unmapped_only}
                checkedIcon={false}
                uncheckedIcon={false}
                onChange={() => setUnmappedOnly(!unmapped_only)}
              />
            </Form.Group>
          </Col>
          <Col md={5}>
            <Form.Group>
              <Form.Label>&nbsp;</Form.Label>
              {hasUserPermissions([UserPermission.EXPORT_PRODUCT_DATA]) && (
                <div>
                  <Button
                    onClick={() =>
                      exportBrandDpProducts({
                        brand_ids: selectedBrands?.map(brand => brand.value),
                        report_type: ReportType.BRAND_DP_PRODUCTS_REPORT,
                      })
                    }
                    disabled={exportingBrandDpProducts || !selectedBrands?.length}
                  >
                    Export Brand DP Products
                  </Button>
                </div>
              )}
            </Form.Group>
          </Col>
        </Row>
      </div>
      <div className="my-2 d-flex justify-content-between align-items-center">
        <p className="mb-0">{dp_products_count} products found</p>
        <PaginationComponent
          totalItems={dp_products_count}
          itemsPerPage={itemsPerPage}
          setItemsPerPage={setItemsPerPage}
          currentPage={currentPage}
          setCurrentPage={setCurrentPage}
          defaultSize
        />
      </div>
      {loadingBrandDpProducts ? (
        <LoadingSpinner />
      ) : (
        <Table className={css(styles.tableText)}>
          <thead className={css(styles.thead)}>
            <tr>
              <th>DP Name</th>
              <th>SKU</th>
              <th>Product Name</th>
              <th>Brand Name</th>
              <th>Pack Size</th>
              <th />
            </tr>
          </thead>
          <tbody className={css(styles.tbody)}>
            {dp_products?.length ? (
              dp_products.map((product, index) => (
                <tr key={index}>
                  <td>{product.global_vendor?.name}</td>
                  <td>{product.vendor_sku}</td>
                  <td>{product.name}</td>
                  <td>{product.brand.name}</td>
                  <td>{product.pack_size || '-'}</td>
                  <td>
                    <Button
                      variant="link"
                      key={index}
                      className={pointerOnHover}
                      onClick={() => {
                        window.open(
                          `/global-product-detail/${product.global_product_id}/` +
                            `${product.id}/distributor`,
                          '_blank',
                        );
                      }}
                    >
                      View
                    </Button>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="100%" className="text-center">
                  No products found.
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      )}
    </Container>
  );
}

const styles = StyleSheet.create({
  tableText: {
    fontSize: '0.95rem',
  },
  thead: {
    position: 'sticky-top',
    zIndex: 1,
    backgroundColor: backgroundGrey,
  },
  tbody: {
    zIndex: 0,
    position: 'relative',
  },
});

LinkedDpProductsView.propTypes = {
  brands: PropTypes.array.isRequired,
  manufacturerId: PropTypes.number.isRequired,
};

export default LinkedDpProductsView;
